import "./CountryCarousel.scss";

import React, { useEffect } from "react";
import Carousel from "nuka-carousel";
import { useWindowSize } from "../../../../../../../hooks/window-hooks";
import {
  SVG_ICONS,
  Svg,
} from "../../../../../../../components/__common/Svg/Svg";
import { Container } from "../../../../../../../components/Grid/Grid";
import { Typography } from "../../../../../../../components/__common/Typography/Typography";
import { Button } from "../../../../../../../components/__common/_controls";
import { Trans, useTranslation } from "react-i18next";
import { Image } from "../../../../../../../components/__common/Image";
import { COUNTRY_FLAGS } from "../../../../../../../components/__common/Image/flagImages";
import { CountriesWithLocalPrivacy } from "../../../../../../../data/landing-page";
import {
  EVENT_CATEGORY_LANDING_PAGE,
  EVENT_LANDING_PAGE_DEFAULT_PRIVACY,
  EVENT_LANDING_PAGE_LOCAL_PRIVACY,
} from "../../../../../../../constants/analytics";
import triggerGAEvent from "../../../../../../../utils/UtilsTriggerGAEvent";
import { useLayout } from "../../../../../../../hooks/layout-hooks";
import { HubCountry } from "../../../../../../../types/landing-page";
import { useSelector } from "react-redux";
import { ApplicationState } from "../../../../../../../store";
import { LayoutState } from "../../../../../../../store/reducers/layout-reducer";
import { UserState } from "../../../../../../../store/reducers/user-reducer";

interface Props {
  setModalOpen: (value: boolean) => void;
}

const CountryCarousel: React.FC<Props> = ({ setModalOpen }) => {
  const { isMobile } = useWindowSize();
  const { userCountryCode } = useSelector<ApplicationState, UserState>(
    (state) => state.user
  );
  const { selectedCountry, isRTL } = useSelector<ApplicationState, LayoutState>(
    (state) => state.layout
  );
  const { setSelectedCountry, getUserCountry } = useLayout();

  const handleGAEvent = () => {
    const hasLocalPrivacy = CountriesWithLocalPrivacy.some(
      (country) => country.code === selectedCountry.code
    );
    const gaAction = hasLocalPrivacy
      ? `${EVENT_LANDING_PAGE_LOCAL_PRIVACY}-${selectedCountry.code}`
      : EVENT_LANDING_PAGE_DEFAULT_PRIVACY;

    triggerGAEvent(EVENT_CATEGORY_LANDING_PAGE, gaAction);
  };

  const handlePrivacySite = () => {
    const hasLocalPrivacy = CountriesWithLocalPrivacy.some(
      (country) => country.code === selectedCountry.code
    );

    if (hasLocalPrivacy && selectedCountry.link) {
      window.open(selectedCountry.link, "_self");
    }
  };

  useEffect(() => {
    if (userCountryCode) {
      const country = getUserCountry();
      setSelectedCountry(country);
    }
  }, [userCountryCode]);

  return (
    <div dir="ltr">
      <Carousel
        wrapAround={true}
        className="country-carousel"
        defaultControlsConfig={{
          containerClassName: "country-carousel__controls",
          pagingDotsContainerClassName: "country-carousel__dots",
          pagingDotsClassName: "country-carousel__dots__dot",
        }}
        renderCenterLeftControls={({ previousSlide }) => (
          <Svg
            icon={
              isMobile
                ? SVG_ICONS.CHEVRON_HUB_LEFT_MOBILE
                : SVG_ICONS.CHEVRON_HUB_LEFT
            }
            width={50}
            height={50}
            className="country-carousel__controls__button"
            onClick={previousSlide}
          />
        )}
        renderCenterRightControls={({ nextSlide }) => (
          <Svg
            icon={
              isMobile
                ? SVG_ICONS.CHEVRON_HUB_RIGHT_MOBILE
                : SVG_ICONS.CHEVRON_HUB_RIGHT
            }
            width={50}
            height={50}
            className="country-carousel__controls__button"
            onClick={nextSlide}
          />
        )}
        slideIndex={isRTL ? 1 : 0}
      >
        {!isRTL ? (
          <FlagContent
            userCountry={selectedCountry}
            handleGAEvent={handleGAEvent}
            handlePrivacySite={handlePrivacySite}
          />
        ) : (
          <MapContent setModalOpen={setModalOpen} />
        )}

        {!isRTL ? (
          <MapContent setModalOpen={setModalOpen} />
        ) : (
          <FlagContent
            userCountry={selectedCountry}
            handleGAEvent={handleGAEvent}
            handlePrivacySite={handlePrivacySite}
          />
        )}
      </Carousel>
    </div>
  );
};

export default CountryCarousel;

interface FlagContentProps {
  userCountry: HubCountry;
  handleGAEvent: () => void;
  handlePrivacySite: () => void;
}

const FlagContent: React.FC<FlagContentProps> = ({
  userCountry,
  handleGAEvent,
  handlePrivacySite,
}) => {
  const { t } = useTranslation();
  const { selectedCountry } = useSelector<ApplicationState, LayoutState>(
    (state) => state.layout
  );
  return (
    <Container className="country-carousel__content">
      <Image image={COUNTRY_FLAGS[selectedCountry.code]} width={88} />
      <Typography
        variant="title1"
        className="country-carousel__content__country"
        weight="wMedium"
      >
        <Trans
          i18nKey="PRIVACY_HUB_COUNTRY_DISPLAY"
          components={{
            span: <span></span>,
          }}
          values={{ "%s": userCountry.name }}
        />
      </Typography>
      <Typography
        variant="title1"
        className="country-carousel__content__text"
        weight="wMedium"
      >
        {t("PRIVACY_HUB_COUNTRY_CONNECT_TEXT")}
      </Typography>
      <Button
        title={t("PRIVACY_HUB_COUNTRY_BUTTON")}
        className="country-carousel__content__button"
        onClick={() => {
          handleGAEvent();
          handlePrivacySite();
        }}
        icon={SVG_ICONS.ARROW_RIGHT}
        iconPosition="right"
        type="content"
      />
    </Container>
  );
};

interface MapContentProps {
  setModalOpen: (value: boolean) => void;
}

const MapContent: React.FC<MapContentProps> = ({ setModalOpen }) => {
  const { t } = useTranslation();

  return (
    <Container className="country-carousel__content">
      <Svg icon={SVG_ICONS.HUB_WORLD_MAP} variant="empty" />
      <Typography
        variant="title1"
        className="country-carousel__content__text"
        weight="wMedium"
      >
        {t("PRIVACY_HUB_PREFERRED_COUNTRY_TEXT")}
      </Typography>
      <Button
        title={t("PRIVACY_HUB_PREFERRED_COUNTRY_BUTTON")}
        className="country-carousel__content__button"
        onClick={() => setModalOpen(true)}
        icon={SVG_ICONS.ARROW_RIGHT}
        iconPosition="right"
        type="content"
      />
    </Container>
  );
};
